import type { AccountInfo, AuthenticationResult, EventMessage, InteractionStatus, RedirectRequest } from "@azure/msal-browser";
import { EMPTY, Observable, of, throwError } from "rxjs";
import { AUTH_REQUIRED_KEY, LOGIN_PENDING_KEY } from "@commonHelpers/auth-needed";
import type { IAuthClient, IAuthEvents, IAuthRouteGuard } from "@commonInterfaces/iAuthAccess";

// Fassung für Besucher ohne Anmeldung - und damit für jeden Crawler.
//
// Wird bereitgestellt, wenn beim Start weder MSAL-Schlüssel im localStorage liegen noch die
// Adresse einen Redirect-Rücksprung trägt. Dann bleibt @azure/msal-* ungeladen.
//
// Kein Fehlerwerfen bei getAllAccounts oder den Observables: die Kopfzeile und der
// Interceptor fragen den Zustand beim Rendern ab und müssen "nicht angemeldet" als
// reguläres Ergebnis bekommen. Die Methoden, die eine Anmeldung VERLANGEN, werfen dagegen -
// sie dürfen nie erreicht werden, weil AuthBootstrap vorher auf den MSAL-Zweig umschaltet.

const requiresLoadedMsal = (method: string) =>
	throwError(() => new Error(`${method} ohne geladenes MSAL aufgerufen - AuthBootstrap hätte vorher umschalten müssen`));

export class NoAuthClient implements IAuthClient {
	public readonly isAuthenticationAvailable = false;

	public initialize(): Observable<void> {
		return of(undefined);
	}

	public handleRedirect(): Observable<AuthenticationResult | null> {
		// Ohne Redirect-Parameter in der Adresse gibt es nichts zu verarbeiten.
		return of(null);
	}

	/**
	 * Anmelden ohne geladenes MSAL: die Anfrage wird hinterlegt und die Seite neu geladen.
	 * Beim nächsten Start meldet isAuthenticationNeededAtStartup() den Bedarf, der MSAL-Zweig
	 * greift und spielt die Anfrage ab.
	 *
	 * Das Neuladen ist unschädlich, weil eine Anmeldung ohnehin auf b2clogin.com umleitet -
	 * der Nutzer verlässt die Seite in jedem Fall. Sichtbar ist nur, dass es passiert, bevor
	 * die Umleitung beginnt.
	 */
	public loginRedirect(request: RedirectRequest): Observable<void> {
		sessionStorage.setItem(LOGIN_PENDING_KEY, JSON.stringify(request));
		window.location.reload();
		// Kein Wert mehr: die Seite wird ersetzt.
		return EMPTY;
	}

	public logoutRedirect(): Observable<void> {
		return requiresLoadedMsal("logoutRedirect");
	}

	public ssoSilent(): Observable<AuthenticationResult> {
		return requiresLoadedMsal("ssoSilent");
	}

	public acquireTokenSilent(): Observable<AuthenticationResult> {
		return requiresLoadedMsal("acquireTokenSilent");
	}

	public acquireTokenPopup(): Observable<AuthenticationResult> {
		return requiresLoadedMsal("acquireTokenPopup");
	}

	public getAllAccounts(): AccountInfo[] {
		return [];
	}

	public isInteractionRequired(): boolean {
		return false;
	}
}

export class NoAuthRouteGuard implements IAuthRouteGuard {
	/**
	 * Ohne geladenes MSAL kann hier nicht entschieden werden, ob der Nutzer Zugriff hat.
	 * Deshalb: Ziel merken, Seite neu laden. Beim nächsten Start meldet
	 * isAuthenticationNeededAtStartup() den Bedarf, der MSAL-Zweig greift, und der echte
	 * Routenschutz entscheidet mit vollem Wissen.
	 */
	public canActivate(): boolean {
		sessionStorage.setItem(AUTH_REQUIRED_KEY, window.location.pathname + window.location.search);
		window.location.reload();
		return false;
	}
}

export class NoAuthEvents implements IAuthEvents {
	// Still: ohne MSAL treten keine Auth-Ereignisse auf.
	public readonly events$: Observable<EventMessage> = EMPTY;
	// Einmal "keine Interaktion läuft" - darauf warten die subscribeState-Handler beider Apps,
	// um den Anmeldezustand als final zu behandeln. Der Literalwert entspricht
	// InteractionStatus.None; InteractionStatus ist ein Const-Objekt, deshalb genügt der Cast
	// auf den Typ ohne Wertimport von msal-browser.
	public readonly progress$: Observable<InteractionStatus> = of("none" as InteractionStatus);
}
